<template>
  <div>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Dataset</label>
      </b-col>
      <b-col cols="6">
        <b-select
          v-model="selectedDatasetId"
          :options="datasets"
          value-field="id"
          text-field="datasetName"
          size="sm"
          @change="loadDataset"
        ></b-select>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col cols="6">
        <label>Title</label>
      </b-col>
      <b-col cols="6">
        <b-input v-model="title" debounce="500" size="sm" />
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col cols="6">
        <label>Chart Width</label>
      </b-col>
      <b-col cols="6">
        <b-select
          v-model="selectedWidth"
          size="sm"
          :options="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]"
          style="width: 100px;"
        ></b-select>
        <b-icon-info-circle
          id="tooltip-selected-width"
          class="text-info ml-2 clickable"
        />
        <b-tooltip target="tooltip-selected-width" triggers="hover">
          <div>Width is responsive to 12 units per row</div>
        </b-tooltip>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Chart orientation</label>
      </b-col>
      <b-col cols="6">
        <b-select
          v-model="axis"
          size="sm"
          :options="indexAxis"
          value-field="key"
          text-field="label"
        ></b-select>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Axis number format</label>
      </b-col>
      <b-col cols="6">
        <b-form-input
          v-model="axisFormat"
          size="sm"
          style="width: 100px;"
          class="d-inline-block"
          placeholder="0a"
        ></b-form-input>
        <b-icon-info-circle
            id="tooltip-axis-format"
          v-b-tooltip.hover
          class="text-info ml-2 clickable"
        />
        <b-tooltip target="tooltip-axis-format" triggers="hover">
          <div>For example value of 12345</div>
          <div>'0.0a' will show as 12.3k</div>
          <div>'0.00' will show as 12345.00</div>
          <div>'0,0.0' will show as 12,345.0</div>
          <div>'0.0%' will show as 1234500.0%</div>
        </b-tooltip>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Value number format</label>
      </b-col>
      <b-col cols="6">
        <b-form-input
          v-model="valueFormat"
          size="sm"
          style="width: 100px;"
          class="d-inline-block"
          placeholder="0a"
        ></b-form-input>
        <b-icon-info-circle
            id="tooltip-value-format"
          v-b-tooltip.hover
          class="text-info ml-2 clickable"
        />
        <b-tooltip target="tooltip-value-format" triggers="hover">
          <div>For example value of 12345</div>
          <div>'0.0a' will show as 12.3k</div>
          <div>'0.00' will show as 12345.00</div>
          <div>'0,0.0' will show as 12,345.0</div>
          <div>'0.0%' will show as 1234500.0%</div>
        </b-tooltip>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Chart Type</label>
      </b-col>
      <b-col cols="6">
        <b-select
          v-model="selectedChartType"
          :options="chartTypes"
          value-field="key"
          text-field="label"
          size="sm"
        ></b-select>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Order By</label>
      </b-col>
      <b-col cols="4">
        <b-select
          v-model="orderBy[0]"
          :options="sortableFields"
          value-field="key"
          text-field="label"
          size="sm"
          @change="orderDirection[0] = 'asc'"
        ></b-select>
      </b-col>
      <b-col cols="2">
        <b-select
          v-if="orderBy[0]"
          v-model="orderDirection[0]"
          :options="['asc', 'desc']"
          size="sm"
        ></b-select>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="6">
        <label>Dimension</label>
      </b-col>
      <b-col cols="6">
        <b-select
          v-model="selectedDimension"
          :options="dimensionFields"
          value-field="key"
          text-field="label"
          size="sm"
        ></b-select>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col cols="12"
        ><span>Measures</span>
        <b-icon-plus-square
          variant="primary"
          class="ml-3 text-muted clickable"
          @click="addMeasure"
        />
      </b-col>
    </b-row>
    <b-row
      v-for="(measure, index) in selectedMeasures"
      :key="index"
      class="mb-2"
    >
      <b-col>
        <b-row class="mb-2">
          <b-col cols="6">
            <label>Measure</label>
          </b-col>
          <b-col cols="5">
            <b-select
              v-model="measure.measureKey"
              :options="measureFields"
              value-field="key"
              text-field="label"
              size="sm"
            ></b-select>
          </b-col>
          <b-col cols="1">
            <b-icon-x
              class="clickable"
              @click="
                selectedMeasures.splice(selectedMeasures.indexOf(measure), 1)
              "
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="6">
            <label>BackgroundColor</label>
          </b-col>
          <b-col cols="5">
            <color-handler v-model="measure.backgroundColor" />
          </b-col>
          <b-col cols="1"></b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col :cols="selectedWidth">
        <chart-viewer
          :type="selectedChartType"
          :data="chartData"
          :options="chartOptions"
        />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col>
        <b-button
          v-if="Boolean(chartSettings)"
          size="sm"
          variant="primary"
          class="float-right"
          @click="editChart"
          >Edit Chart
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="primary"
          class="float-right"
          @click="addChart"
          >Add Chart
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/*global _*/
// import BarChart from '@/components/charts/BarChart.js';
import ChartViewer from '@/components/charts/ChartViewer';
import ColorHandler from '@/components/ColorHandler';
import application from '@/mixins/application';
import numeral from 'numeral';

export default {
  //emits, btn-clicked, form-submit
  name: 'ChartBuilder',
  mixins: [application],
  components: { ChartViewer, ColorHandler },
  props: {
    datasets: Array,
    chartSettings: Object,
  },
  data() {
    return {
      indexAxis: [
        { key: 'y', label: 'Vertical' },
        { key: 'x', label: 'Horizontal' },
      ],
      chartTypes: [
        { key: 'bar', label: 'Bar' },
        { key: 'line', label: 'Line' },
        // { key: 'pie', label: 'Pie' },
      ],
      selectedDimension: '',
      dataset: {
        datasetFields: [],
        datasetCachedData: [],
      },

      selectedDatasetId: -1,
      selectedWidth: 6,
      selectedChartType: 'bar',
      selectedDimensions: [],
      selectedMeasures: [],
      title: '',
      valueFormat: '0.0a',
      axisFormat: '0a',
      axis: 'y',
      orderBy: [null],
      orderDirection: ['asc'],
    };
  },
  mounted() {
    if (this.chartSettings) {
      const chartSettings = _.cloneDeep(this.chartSettings);
      this.selectedWidth = chartSettings.width || 6;
      if (chartSettings.selectedDimensions) {
        chartSettings.selectedDimensions = JSON.parse(
          chartSettings.selectedDimensions
        );
      }
      if (chartSettings.selectedMeasures) {
        chartSettings.selectedMeasures = JSON.parse(
          chartSettings.selectedMeasures
        );
      }
      if (chartSettings.options) {
        chartSettings.options = JSON.parse(chartSettings.options);
      }
      if (chartSettings.orderBy) {
        chartSettings.orderBy = JSON.parse(chartSettings.orderBy);
      }
      if (chartSettings.orderDirection) {
        chartSettings.orderDirection = JSON.parse(chartSettings.orderDirection);
      }

      this.selectedDatasetId = chartSettings.reportDatasetId;
      const vm = this;
      vm.loadDataset().then(() => {
        this.selectedChartType = chartSettings.chartType;
        this.selectedDimension =
          chartSettings.selectedDimensions[0].dimensionKey;
        this.selectedMeasures = chartSettings.selectedMeasures;
        this.options = chartSettings.options;
        this.title = chartSettings.title;
        this.axis = chartSettings.axis || 'y';
        this.valueFormat = chartSettings.valueFormat;
        this.axisFormat = chartSettings.axisFormat;
        this.orderBy = chartSettings.orderBy || [null];
        this.orderDirection = chartSettings.orderDirection || ['asc'];
        _.each(this.selectedMeasures, measure => {
          if (!measure.backgroundColor) {
            measure.backgroundColor = '#ABB8C3';
          }
        });
      });
    } else {
      this.selectedDatasetId = this.datasets[0].id;
      this.loadDataset();
    }
  },
  computed: {
    chartOptions() {
      const options = {};
      options.plugins = {};
      const chartSettings = this;
      const title = chartSettings.title;
      const axisFormat = chartSettings.axisFormat;
      const valueFormat = chartSettings.valueFormat;
      const axis = chartSettings.axis;

      if (title) {
        options.plugins.title = { display: true, text: title };
      }

      if (axisFormat) {
        options.scales = {};
        options.scales[axis] = {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return numeral(value).format(axisFormat);
            },
          },
        };
      }

      if (valueFormat) {
        options.plugins.tooltip = {
          callbacks: {
            label: function(context) {
              let value = context.parsed[axis] || '';
              if (value) {
                return numeral(value).format(valueFormat);
              }
              return value;
            },
          },
        };
      }

      options.indexAxis = axis === 'y' ? 'x' : 'y';

      return options;
    },
    dimensionFields() {
      return this.dataset.datasetFields.filter(
        field => field.class === 'smartgrabber-dimension'
      );
    },
    measureFields() {
      return this.dataset.datasetFields.filter(
        field => field.class === 'smartgrabber-measure'
      );
    },
    sortableFields() {
      return [
        ...[{key: null, label: 'No order'}],
        ...this.dataset.datasetFields.filter(field => {
          return (
            _.some(this.selectedMeasures, { measureKey: field.key }) ||
            this.selectedDimension === field.key
          );
        }),
      ];
    },
    chartData() {
      try {
        const rows = _.cloneDeep(this.dataset.datasetCachedData);
        const measureFields = _.cloneDeep(this.measureFields);
        if (
          rows &&
          this.selectedDimensions.length > 0 &&
          this.selectedMeasures.length > 0
        ) {
          const chartData = {};
          const dimensionKey = this.selectedDimension;
          const axis = this.axis;
          const orderBy = this.orderBy;
          const orderDirection = this.orderDirection;
          
          let aggData = aggSum(dimensionKey, measureFields, rows, axis);
          
          if(orderBy[0]) {
            const field = _.find(this.dataset.datasetFields, {key: orderBy[0]});
            const order = [];
            if(field.class === 'smartgrabber-dimension') {
              order.push(axis === 'y' ? 'x' : 'y');
            } else {
              order.push(field.label);
            }
            aggData = _.orderBy(aggData, order, orderDirection);
          }

          //chartData.labels = _.map(aggData, row => row[dimensionName]);
          chartData.datasets = [];
          _.each(this.selectedMeasures, selectedMeasure => {
            const meaName = _.find(measureFields, {
              key: selectedMeasure.measureKey,
            }).label;
            let measureData = {
              label: meaName,
              data: aggData,
              parsing: {},
            };
            measureData.parsing[axis + 'AxisKey'] = meaName;
            if (selectedMeasure.backgroundColor) {
              const chartType = this.selectedChartType;
              if (chartType === 'line') {
                measureData.backgroundColor = selectedMeasure.backgroundColor;
                measureData.borderColor =
                  selectedMeasure.backgroundColor + '99';
              } else {
                measureData.backgroundColor =
                  selectedMeasure.backgroundColor + '99';
                measureData.borderColor = selectedMeasure.backgroundColor;
              }
            }
            chartData.datasets.push(measureData);
          });
          chartData.options = {
            scales: {},
          };
          chartData.options.scales[this.axis] = { beginAtZero: true };
          return chartData;
        } else {
          return {};
        }
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.log(ex);
        return {};
      }
    },
  },
  methods: {
    loadDataset() {
      const vm = this;
      return vm.axios
        .get('/api/datasets/' + vm.selectedDatasetId)
        .then(resp => {
          if (resp.data) {
            const dataset = _.cloneDeep(resp.data);
            dataset.datasetCachedData = JSON.parse(dataset.datasetCachedData);
            dataset.datasetFields = JSON.parse(dataset.datasetFields);
            vm.dataset = dataset;
          } else vm.dataset = null;
        })
        .catch(() => {
          vm.PromptWarning(
            'Failed',
            'Failed to load the data. Please try again.'
          );
        })
        .then(() => {
          vm.selectedDimension = vm.dimensionFields[0].key;
          vm.selectedDimensions = [vm.dimensionFields[0].key];
          vm.selectedMeasures = [];
          vm.addMeasure();
        });
    },
    addMeasure() {
      this.selectedMeasures.push({
        measureKey: this.measureFields[0].key,
        backgroundColor: '#ABB8C3',
      });
    },
    addChart() {
      const selectedDimensions = JSON.stringify([
        { dimensionKey: this.selectedDimension.toString() },
      ]);
      let chart = {
        chartType: this.selectedChartType,
        reportDatasetId: this.selectedDatasetId,
        selectedDimensions: selectedDimensions,
        selectedMeasures: JSON.stringify(this.selectedMeasures),
        options: JSON.stringify({}),
        width: this.selectedWidth,
        title: this.title,
        valueFormat: this.valueFormat,
        axisFormat: this.axisFormat,
        axis: this.axis,
        orderBy: JSON.stringify(this.orderBy),
        orderDirection: JSON.stringify(this.orderDirection),
      };
      this.$emit('addChart', chart);
    },
    editChart() {
      const selectedDimensions = JSON.stringify([
        { dimensionKey: this.selectedDimension.toString() },
      ]);
      let chart = {
        chartType: this.selectedChartType,
        reportDatasetId: this.selectedDatasetId,
        selectedDimensions: selectedDimensions,
        selectedMeasures: JSON.stringify(this.selectedMeasures),
        options: JSON.stringify({}),
        width: this.selectedWidth,
        title: this.title,
        valueFormat: this.valueFormat,
        axisFormat: this.axisFormat,
        axis: this.axis,
        orderBy: JSON.stringify(this.orderBy),
        orderDirection: JSON.stringify(this.orderDirection),
      };
      this.$emit('editChart', chart, this.chartSettings.index);
    },
  },
};

function aggSum(dimKey, measureFields, rows, axis) {
  const dataAxis = axis === 'y' ? 'x' : 'y';
  return _.values(
    _.reduce(
      rows,
      (result, row) => {
        const rowData = {};

        const dimVal = row[dimKey];
        rowData[dataAxis] = dimVal;
        _.each(measureFields, measureField => {
          const meaKey = measureField.key;
          const meaName = measureField.label;
          const meaVal = row[meaKey];
          rowData[meaName] =
            parseFloat(meaVal) + (result[dimVal] ? result[dimVal][meaName] : 0);
        });
        result[dimVal] = rowData;
        return result;
      },
      {}
    )
  );
}
</script>